import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, User, Heart, Users, Sparkles, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services, heroImages, businessInfo } from '../data/mock';
import SEO from '../components/SEO';

const Services = () => {
  const getServiceIcon = (iconName) => {
    const icons = {
      User: <User className="h-8 w-8" />,
      Heart: <Heart className="h-8 w-8" />,
      Users: <Users className="h-8 w-8" />,
      Rings: <Heart className="h-8 w-8" />,
      Sparkles: <Sparkles className="h-8 w-8" />,
    };
    return icons[iconName] || <Heart className="h-8 w-8" />;
  };

  const getServiceColor = (index) => {
    const colors = [
      'from-teal-500 to-teal-600',
      'from-slate-600 to-slate-700',
      'from-teal-600 to-teal-700',
      'from-slate-500 to-slate-600',
      'from-teal-500 to-slate-600',
    ];
    return colors[index % colors.length];
  };

  return (
    <>
      <SEO 
        title="Therapy Services - Individual, Couples, Family & Teen Counseling"
        description="Comprehensive therapy services in Lebanon, PA including individual therapy, couples counseling, family therapy, premarital counseling, and teen therapy. Evidence-based, compassionate care tailored to your needs."
        keywords="individual therapy Lebanon PA, couples therapy, family counseling, premarital counseling, teen therapy, marriage counseling Lebanon Pennsylvania"
        url="/services"
      />
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-teal-50 via-white to-slate-50">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-teal-100/30 to-transparent" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <img 
              src={businessInfo.logo} 
              alt={businessInfo.name}
              className="h-32 w-auto mx-auto mb-6"
            />
            <span className="text-teal-600 font-medium tracking-wide uppercase text-sm">Our Services</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 mt-2 mb-6">
              Therapeutic Services
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We offer comprehensive therapy services tailored to meet you where you are in your journey toward healing and growth.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, index) => (
              <div 
                key={service.id}
                className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              >
                {/* Content Side */}
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className={`inline-flex items-center gap-3 px-4 py-2 rounded-full bg-gradient-to-r ${getServiceColor(index)} text-white`}>
                    {getServiceIcon(service.icon)}
                    <span className="font-medium">Service</span>
                  </div>
                  
                  <h2 className="text-3xl sm:text-4xl font-bold text-slate-800">
                    {service.title}
                  </h2>
                  
                  <p className="text-lg text-slate-600 leading-relaxed">
                    {service.fullDescription}
                  </p>
                  
                  {/* Topics */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800 uppercase tracking-wide mb-4">Areas of Focus</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {service.topics.map((topic, topicIndex) => (
                        <div key={topicIndex} className="flex items-center gap-2 text-slate-600">
                          <CheckCircle2 className="h-4 w-4 text-teal-500 flex-shrink-0" />
                          <span className="text-sm">{topic}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <Link to="/contact">
                    <Button className="bg-teal-600 hover:bg-teal-700 text-white mt-4">
                      Schedule Consultation
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
                
                {/* Visual Side */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <Card className={`border-0 overflow-hidden shadow-2xl bg-gradient-to-br ${getServiceColor(index)}`}>
                    <CardContent className="p-12">
                      <div className="text-center text-white">
                        <div className="w-24 h-24 bg-white/20 rounded-3xl flex items-center justify-center mx-auto mb-6 backdrop-blur-sm">
                          {getServiceIcon(service.icon)}
                        </div>
                        <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                        <p className="text-white/80 leading-relaxed">
                          {service.shortDescription}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-teal-600 font-medium tracking-wide uppercase text-sm">What to Expect</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mt-2 mb-4">
              Your Therapy Journey
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Initial Consultation", description: "We'll discuss your needs and determine if we're a good fit." },
              { step: "2", title: "Assessment", description: "Together, we'll explore your history and current concerns." },
              { step: "3", title: "Treatment Plan", description: "We'll create a personalized plan tailored to your goals." },
              { step: "4", title: "Ongoing Support", description: "Regular sessions to support your growth and progress." },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 bg-teal-500 text-white rounded-2xl flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Not Sure Which Service Is Right for You?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            That's okay! During your initial consultation, we'll discuss your needs and determine the best approach for your unique situation.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-6 text-lg">
              Get Started Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default Services;
